/* High Rise — Seltzers PDP (4-pack) state machine. Same shape as
   landing-bottles.js but with 6 flavors and per-flavor strength
   availability. */
(() => {
  'use strict';
  const root = document.querySelector('[data-hr-landing][data-hr-page="seltzers"]');
  if (!root) return;

  const DATA = window.HR_SELTZER_DATA || {};
  // Deep-link support: `?p=mango` etc. selects that flavor on load.
  // Liquid can't read query params in /pages/ context (request.url
  // is nil), so we resolve it client-side.
  const URL_FLAVORS = ['blueberry', 'bloodorange', 'pineapple', 'raspberry', 'grapefruit', 'mango'];
  const urlFlavor = new URLSearchParams(location.search).get('p');
  let currentFlavor = (URL_FLAVORS.includes(urlFlavor) ? urlFlavor : null)
                   || window.HR_SELTZER_DEFAULT_FLAVOR
                   || 'blueberry';
  let currentStrength = '5';

  const $  = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => [...el.querySelectorAll(s)];
  const setText = (id, v) => { const e = document.getElementById(id); if (e) e.textContent = v; };
  const setHTML = (id, v) => { const e = document.getElementById(id); if (e) e.innerHTML = v; };

  // Shared spec rows that are identical across flavors
  const COMMON_SPECS = (liftRow, profileRow) => [
    ['Lift / can',  liftRow],
    ['Plant blend', 'THC + CBD + CBG (ratio shifts by dose)'],
    ['Pack',        '12oz × 4 cans'],
    ['Profile',     profileRow],
    ['Calories',    '~20 per can'],
    ['Ingredients', 'Real fruit · Organic cane sugar · Hemp-derived botanicals'],
    ['Free from',   'Alcohol · Gluten · GMOs · Artificial flavors'],
  ];

  const COPY = {
    blueberry: {
      title: 'Blueberry',
      tagline: 'Bright, jammy, made for slow-rolling Saturdays.',
      tagline2: 'Lush blueberry, gentle lift, a smooth alcohol-free pour.',
      info: `High Rise Blueberry is a fresh, fruit-forward, non-alcoholic seltzer. Real blueberry meets a soft natural sweetness — vibrant but refined. Made with real fruit and organic cane sugar, it finishes smooth and crisp with a clean, easy lift.`,
      pull: 'Pick your lift level. Pour. Stay easy.',
      roast: 'Lush · Blueberry',
      specs: COMMON_SPECS('3mg, 5mg, or 10mg', 'Lush · Blueberry · Smooth finish'),
      strengths: ['3','5','10'], default: '5', themeColor: '#81B0D2'
    },
    bloodorange: {
      title: 'Blood Orange',
      tagline: 'Citrus-forward and just a little wild.',
      tagline2: 'Real blood orange — bright, juicy, just a touch wild.',
      info: `Bright, juicy, just a touch wild. Real blood orange and organic cane sugar, balanced with a smooth, alcohol-free lift. Made for hosts and good-times sippers.`,
      pull: 'Citrus-forward. Slow-rolling. Made for the moment.',
      roast: 'Citrus · Blood Orange',
      specs: COMMON_SPECS('5mg or 10mg', 'Bright · Citrus · Juicy'),
      strengths: ['5','10'], default: '5', themeColor: '#FF6A39'
    },
    pineapple: {
      title: 'Pineapple',
      tagline: 'Tropical, sunlit, made for long Saturdays.',
      tagline2: 'Pineapple — sunlit, tropical, made for the long Saturday.',
      info: `Tropical and sunlit. Real pineapple meets a soft cane sugar finish — bright, lively, and crafted for golden afternoons. Easy alcohol-free lift, easy to share.`,
      pull: 'Bright. Tropical. Pour-and-go.',
      roast: 'Tropical · Pineapple',
      specs: COMMON_SPECS('3mg, 5mg, or 10mg', 'Tropical · Pineapple · Crisp'),
      strengths: ['3','5','10'], default: '5', themeColor: '#FED141'
    },
    raspberry: {
      title: 'Raspberry',
      tagline: 'Lush raspberry meets gentle sweetness.',
      tagline2: 'Lush raspberry meets gentle sweetness — vibrant yet refined.',
      info: `Lush raspberry meets gentle natural sweetness. Vibrant yet refined, made with real fruit and organic cane sugar. Smooth, creamy finish with a bright, easy lift.`,
      pull: 'Lush. Bright. Made for connection.',
      roast: 'Lush · Raspberry',
      specs: COMMON_SPECS('10mg', 'Lush · Raspberry · Smooth'),
      strengths: ['10'], default: '10', themeColor: '#9B7793'
    },
    grapefruit: {
      title: 'Grapefruit',
      tagline: 'Bitter-bright. Crisp. The host\'s favorite.',
      tagline2: 'Real grapefruit — bitter-bright, crisp, made for the bar cart.',
      info: `Bitter-bright grapefruit, dialed for the bar cart. Real fruit, organic cane sugar, smooth alcohol-free lift. Pours like a Paloma without the regret.`,
      pull: 'Bitter-bright. Crisp. The hosts\' favorite.',
      roast: 'Citrus · Grapefruit',
      specs: COMMON_SPECS('5mg', 'Bitter-bright · Citrus · Crisp'),
      strengths: ['5'], default: '5', themeColor: '#FF8DA1'
    },
    mango: {
      title: 'Mango',
      tagline: 'Ripe, sunlit, easy to love.',
      tagline2: 'Ripe mango — sunlit, soft, easy to love.',
      info: `Ripe mango, soft sweetness, smooth finish. Made with real fruit and organic cane sugar — sunlit and easy to love. Alcohol-free lift, kind of evening you don't want to end.`,
      pull: 'Ripe. Sunlit. Easy to love.',
      roast: 'Tropical · Mango',
      specs: COMMON_SPECS('3mg or 5mg', 'Ripe · Mango · Soft sweetness'),
      strengths: ['3','5'], default: '5', themeColor: '#EF9600'
    },
  };

  const ASSET_BASE = (() => {
    const probe = document.querySelector('#thumb-front')?.src;
    if (!probe) return '';
    return probe.replace(/landing-[^/]+$/, '');
  })();
  const assetUrl = (filename) => ASSET_BASE + filename;

  // Per-flavor-per-strength image map with prototype fallback rules:
  //  - 3mg variants use their own can + 4pack (label-specific), but
  //    lifestyle + pour fall back to 5mg (brand shots, not strength-bound)
  //  - Raspberry only ships 10mg; lifestyle reuses the pour shot
  //    (no dedicated lifestyle in catalog)
  //  - Grapefruit only ships 5mg; uses its own assets across the board
  const IMG = (flavor, strength) => {
    const f = flavor, s = strength;
    const base = (st, slot) => `landing-${f}-${st}mg-${slot}.png`;
    // 3mg flavors fall back to 5mg lifestyle + pour
    if (s === '3' && (f === 'blueberry' || f === 'pineapple' || f === 'mango')) {
      return {
        front:     base('3', 'can'),
        lifestyle: base('5', 'lifestyle'),
        back:      base('3', '4pack'),
        detail:    base('5', 'pour'),
      };
    }
    // Raspberry 10mg has pour but no lifestyle; reuse pour
    if (f === 'raspberry' && s === '10') {
      return {
        front:     base('10', 'can'),
        lifestyle: base('10', 'pour'),
        back:      base('10', '4pack'),
        detail:    base('10', 'pour'),
      };
    }
    // Default: all slots exist for this flavor+strength
    return {
      front:     base(s, 'can'),
      lifestyle: base(s, 'lifestyle'),
      back:      base(s, '4pack'),
      detail:    base(s, 'pour'),
    };
  };
  const imageSet = (flavor, strength) => IMG(flavor, strength);

  const updateGallery = (flavor, strength) => {
    const imgs = imageSet(flavor, strength);
    ['front', 'lifestyle', 'back', 'detail'].forEach((slot) => {
      const el = document.getElementById('thumb-' + slot);
      if (el) el.src = assetUrl(imgs[slot]);
    });
    const activeSlot = $('#gallery-main')?.getAttribute('data-active-slot') || 'front';
    const main = document.getElementById('gal-main-img');
    if (main) main.src = assetUrl(imgs[activeSlot] || imgs.front);
    if (main) main.alt = `High Rise ${COPY[flavor].title} 12oz 4-pack`;
  };

  const setStrengthAvailability = (flavor) => {
    const f = COPY[flavor];
    let firstAvailable = null;
    $$('.size__pills label').forEach((lab) => {
      const sk = lab.getAttribute('data-strength');
      const inp = lab.querySelector('input');
      const available = f.strengths.includes(sk);
      lab.setAttribute('aria-disabled', String(!available));
      if (inp) {
        inp.disabled = !available;
        if (available && !firstAvailable) firstAvailable = inp;
      }
    });
    const checked = $$('input[name="strength"]').find((i) => i.checked && !i.disabled);
    if (!checked && firstAvailable) firstAvailable.checked = true;
    currentStrength = ($$('input[name="strength"]').find((i) => i.checked)?.dataset.strengthKey) || f.default;
    const note = $('#strength-note');
    if (note) note.textContent = f.strengths.length === 1
      ? `Available at ${f.strengths[0]}mg for this flavor.`
      : f.strengths.length === 2 ? 'Two doses available for this flavor.'
      : 'Three doses available for this flavor.';
  };

  const refreshPrices = () => {
    const variant = DATA[currentFlavor]?.variants?.[currentStrength];
    if (!variant) return;
    const sub = variant.sellingPlans?.[0];
    const onetimeEl = $('[data-onetime-price]');
    const subEl = $('[data-sub-price]');
    const strikeEl = $('[data-strike-price]');
    if (onetimeEl) onetimeEl.textContent = variant.priceFormatted;
    if (sub) {
      if (subEl) subEl.textContent = sub.priceFormatted;
      if (strikeEl) strikeEl.textContent = variant.priceFormatted;
    }
    $$('.purchase-card').forEach((card) => {
      const inp = card.querySelector('input');
      card.setAttribute('data-active', String(!!inp?.checked));
    });
  };

  const swap = (flavor) => {
    if (!COPY[flavor]) return;
    currentFlavor = flavor;
    const f = COPY[flavor];

    root.setAttribute('data-product', flavor);

    $$('.flavor-tile').forEach((t) => {
      const me = t.dataset.handle === flavor;
      t.classList.toggle('is-active', me);
      t.setAttribute('aria-selected', String(me));
    });

    setText('prod-title', f.title);
    setText('prod-title-m', f.title);
    setText('prod-tagline', f.tagline);
    setText('prod-tagline-mobile', f.tagline);

    // Info-band (the "About this pack" section) is flavor-specific.
    // Prototype updates these on every swap — without this, the body
    // stays as the default flavor (Blueberry) no matter what user picks.
    setText('info-title', f.title);
    setText('info-tagline', f.tagline2 || f.tagline);
    setHTML('info-copy', f.info || '');
    setText('info-pull', f.pull || '');
    setText('spec-roast', f.roast || '');

    // Rebuild the spec card rows from the flavor's specs array so
    // "Profile" and "Lift / can" reflect the current flavor.
    const specEl = document.getElementById('spec-rows');
    if (specEl && Array.isArray(f.specs)) {
      specEl.innerHTML = f.specs.map(([k, v]) =>
        `<li><span>${k}</span><strong>${v}</strong></li>`
      ).join('');
    }

    setStrengthAvailability(flavor);
    updateGallery(flavor, currentStrength);
    refreshPrices();

    history.replaceState(null, '', `?p=${flavor}`);
  };

  $$('.flavor-tile').forEach((t) => {
    t.addEventListener('click', (e) => { e.preventDefault(); swap(t.dataset.handle); });
  });

  $$('input[name="strength"]').forEach((i) => {
    i.addEventListener('change', () => {
      currentStrength = i.dataset.strengthKey || '5';
      updateGallery(currentFlavor, currentStrength);
      refreshPrices();
    });
  });

  // .purchase-card is a <label> — clicking it natively toggles the
  // radio inside. We only listen to change, no click handler (avoids
  // double-toggle race that flipped the radio back off).
  $$('input[name="purchase"]').forEach((i) => i.addEventListener('change', refreshPrices));

  $$('.gallery-thumb').forEach((b) => {
    b.addEventListener('click', () => {
      const slot = b.dataset.slot;
      $$('.gallery-thumb').forEach((x) => x.classList.toggle('is-active', x === b));
      const imgs = imageSet(currentFlavor, currentStrength);
      const main = document.getElementById('gal-main-img');
      if (main && imgs[slot]) {
        $('#gallery-main')?.setAttribute('data-active-slot', slot);
        main.src = assetUrl(imgs[slot]);
      }
    });
  });

  $$('[data-qty-dir]').forEach((b) => {
    b.addEventListener('click', () => {
      const inp = $('#qty-input');
      if (!inp) return;
      const dir = Number(b.dataset.qtyDir);
      inp.value = Math.max(1, Math.min(99, Number(inp.value) + dir));
    });
  });

  const form = $('#product-form');
  const atcBtn = $('#hr-atc-btn');
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (atcBtn.getAttribute('aria-busy') === 'true') return;

    const variant = DATA[currentFlavor]?.variants?.[currentStrength];
    if (!variant) {
      root.__hr?.showToast?.('This option is not available.', 'error');
      return;
    }
    const qty = Math.max(1, Number($('#qty-input')?.value || 1));
    const isSub = $$('input[name="purchase"]').find((i) => i.checked)?.value === 'subscribe';
    let sellingPlanId = null;
    if (isSub) {
      const freq = $('#freq-select')?.value;
      // Word-boundary regex so "2 week" doesn't also match "12 weeks" if
      // Highrise ever adds a longer cadence (today plans are "Ships every
      // 2|4|8 weeks" only, so includes() works — but one admin click away).
      const freqRe = new RegExp(`\\b${freq}\\s*weeks?\\b`, 'i');
      const plan = (variant.sellingPlans || []).find((sp) => freqRe.test(sp.name)) || variant.sellingPlans?.[0];
      if (plan) sellingPlanId = plan.id;
    }

    atcBtn.setAttribute('aria-busy', 'true');
    atcBtn.setAttribute('disabled', '');

    try {
      await root.__hr.addToCart({
        variantId: variant.variantId,
        quantity: qty,
        sellingPlanId,
      });
      root.__hr?.showToast?.('Added 4-Pack to cart', 'success');
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') setTimeout(() => drawer.open(atcBtn), 280);
    } catch (err) {
      root.__hr?.showToast?.(err.message || 'Could not add to cart', 'error');
    } finally {
      atcBtn.removeAttribute('aria-busy');
      atcBtn.removeAttribute('disabled');
    }
  });

  swap(currentFlavor);
})();
